import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft,
  Shield,
  Loader2,
  FileText,
  Clock,
  CheckCircle,
  Globe,
  Info,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyCompliancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    fetchCompliance();
  }, []);

  const fetchCompliance = async () => {
    try {
      const response = await axios.get(`${API}/company/compliance`);
      setCompliance(response.data);
    } catch (error) {
      toast.error("Failed to load compliance settings");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary-700 animate-spin" />
      </div>
    );
  }

  const rules = compliance?.rules || {};

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-stone-600"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-stone-200" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-700 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-lg font-semibold text-stone-900">
                    Compliance Settings
                  </h1>
                  <p className="text-xs text-stone-500">
                    View your organization&apos;s compliance profile
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Overview */}
        <Card className="mb-6" data-testid="compliance-profile-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-6 h-6 text-primary-600" />
                <div>
                  <CardTitle>{compliance?.profile_name || "Default"}</CardTitle>
                  <CardDescription>
                    Code: {compliance?.profile_code || "DEFAULT"}
                  </CardDescription>
                </div>
              </div>
              {compliance?.has_overrides && (
                <Badge variant="outline" className="text-amber-600 border-amber-200">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Custom Overrides Applied
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">About Your Compliance Profile</p>
                <p className="text-sm text-blue-700 mt-1">
                  This profile defines the governance rules and requirements for your organization&apos;s meetings and minutes. 
                  Contact your platform administrator to request changes to your compliance settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Minutes Rules */}
        <Card className="mb-6" data-testid="minutes-rules-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-stone-500" />
              Minutes Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RuleItem
                label="Quorum Explicit"
                value={rules.minutes?.require_quorum_explicit}
                description="Quorum must be explicitly confirmed"
              />
              <RuleItem
                label="End Time Required"
                value={rules.minutes?.require_end_time}
                description="Meeting end time must be recorded"
              />
              <RuleItem
                label="Chair Signature Required"
                value={rules.minutes?.require_chair_signature_for_final}
                description="Chair must sign before minutes are finalized"
              />
              <RuleItem
                label="Attendance Confirmation"
                value={rules.minutes?.require_attendance_confirmation}
                description="Attendees must be explicitly confirmed"
              />
              <RuleItem
                label="Agenda Approval"
                value={rules.minutes?.require_agenda_approval}
                description="Agenda must be approved at start of meeting"
              />
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-900">Minutes Retention</p>
                    <p className="text-sm text-stone-500">Minimum retention period</p>
                  </div>
                  <span className="text-lg font-semibold text-primary-700">
                    {rules.minutes?.min_retention_years_minutes || 7} years
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Artifact Retention */}
        <Card className="mb-6" data-testid="retention-rules-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-stone-500" />
              Artifact Retention Periods
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RetentionItem
                label="Audio Recordings"
                months={rules.artifacts?.retain_audio_months}
              />
              <RetentionItem
                label="Transcripts"
                months={rules.artifacts?.retain_transcript_months}
              />
              <RetentionItem
                label="Attachments"
                months={rules.artifacts?.retain_attachments_months}
              />
              <RetentionItem
                label="Exports (PDF/DOCX)"
                months={rules.artifacts?.retain_exports_months}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section Rules */}
        <Card data-testid="section-rules-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="w-5 h-5 text-stone-500" />
              Section Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RuleItem
                label="Matters Arising"
                value={rules.sections?.require_matters_arising_if_prior_exists}
                description="Required if prior action items exist"
              />
              <RuleItem
                label="Action Items Section"
                value={rules.sections?.require_action_items_section}
                description="Action items section is mandatory"
              />
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-900">Resolutions Section</p>
                    <p className="text-sm text-stone-500">When to include resolutions</p>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {rules.sections?.enable_resolutions_section || "auto"}
                  </Badge>
                </div>
              </div>
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-900">Declarations Section</p>
                    <p className="text-sm text-stone-500">When to include declarations</p>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {rules.sections?.enable_declarations_section || "auto"}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Updated */}
        {compliance?.updated_at && (
          <p className="text-sm text-stone-400 text-center mt-6">
            Last updated: {new Date(compliance.updated_at).toLocaleDateString()}
          </p>
        )}
      </main>
    </div>
  );
}

function RuleItem({ label, value, description }) {
  return (
    <div className="p-4 bg-stone-50 rounded-lg">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-stone-900">{label}</p>
          {description && <p className="text-sm text-stone-500">{description}</p>}
        </div>
        {value ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-0">Required</Badge>
        ) : (
          <Badge variant="secondary" className="bg-stone-200 text-stone-600">Optional</Badge>
        )}
      </div>
    </div>
  );
}

function RetentionItem({ label, months }) {
  const years = months ? Math.floor(months / 12) : 0;
  const remainingMonths = months ? months % 12 : 0;
  
  let display = "";
  if (years > 0) display += `${years} year${years > 1 ? "s" : ""}`;
  if (remainingMonths > 0) display += `${years > 0 ? " " : ""}${remainingMonths} month${remainingMonths > 1 ? "s" : ""}`;
  if (!display) display = "Not set";

  return (
    <div className="p-4 bg-stone-50 rounded-lg">
      <div className="flex items-center justify-between">
        <p className="font-medium text-stone-900">{label}</p>
        <span className="text-primary-700 font-semibold">{display}</span>
      </div>
    </div>
  );
}
